import type { FleetGateway } from '../../core/bridge/connect-client';
import type { BackendClient } from '../bridge/backend-client';

/*
 * FleetManager — main-thread proxy for the ONE fleet feature the agent app keeps:
 * "claim a nearby unclaimed gateway." (The full fleet roster / OTA / members /
 * tiers live in a separate org-admin dashboard, TBD — not here.)
 *
 * It owns the nearby-unclaimed set, POLLS for it on a timer (DiscoverNearby is a
 * cheap unary cloud RPC — there's no live stream to subscribe to), and emits
 * `change` so the bell/popup re-render. claim() then re-polls so a just-claimed
 * box drops out of "nearby". All IO lives in the worker; this is a thin proxy +
 * cache + event emitter (Cardinal Rule).
 */
const DISCOVER_INTERVAL_MS = 25_000;

export class FleetManager extends EventTarget {
  private nearbyCache: FleetGateway[] = [];
  private errorMessage = '';
  private timer?: ReturnType<typeof setInterval>;
  private polling = false;

  constructor(private readonly backend: BackendClient) {
    super();
  }

  /** Enrolled-but-unclaimed gateways on the caller's network (last poll). */
  get nearby(): FleetGateway[] {
    return this.nearbyCache;
  }

  /** Last discovery error ('' when the last poll succeeded). */
  get error(): string {
    return this.errorMessage;
  }

  /**
   * Start periodic LAN discovery: an immediate poll, then every ~25s. Idempotent
   * (a second call is a no-op). The view/shell calls this once on connect.
   */
  startDiscovery(): void {
    if (this.timer) return;
    void this.poll();
    this.timer = setInterval(() => void this.poll(), DISCOVER_INTERVAL_MS);
  }

  /** Stop the discovery timer (e.g. on teardown). */
  stopDiscovery(): void {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = undefined;
    }
  }

  /** One discovery poll. Overlap-guarded; records the error but never throws (a
   *  transient cloud blip must not kill the timer). Emits `change` on every poll
   *  so the bell-notification sync reconciles even when the set goes empty. */
  async poll(): Promise<void> {
    if (this.polling) return;
    this.polling = true;
    try {
      this.nearbyCache = await this.backend.api.discoverNearby();
      this.errorMessage = '';
    } catch (e) {
      this.errorMessage = errMsg(e);
    } finally {
      this.polling = false;
      this.dispatchEvent(new CustomEvent('change'));
    }
  }

  /**
   * Claim a nearby gateway into the caller's org. ADMIN-only — SERVER-enforced
   * (the UI also gates the button via authz.can). The quick path passes NO code:
   * the server proves the claim by LAN-presence (the caller is on the same network
   * the box enrolled from). `claimCode` is the OPTIONAL stronger/cross-network
   * proof — the future "open the gateway access point, enter the 4 digits" step —
   * already wired here, just empty today. On success, re-polls so the box drops
   * out of "nearby". Throws on failure (incl. a member's PermissionDenied).
   */
  async claim(deviceFingerprint: string, displayName: string, claimCode = ''): Promise<void> {
    await this.backend.api.claimGateway(deviceFingerprint, claimCode, displayName);
    await this.poll();
  }
}

function errMsg(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}
